/**
 * Tooltip — Small floating label shown on hover/focus to describe an element.
 *
 * Tooltip Component Guidelines
 * - Use Tooltip for brief, supplementary labels (1-2 lines max). For richer content
 *   with actions or longer text, use Popover instead.
 * - Always add a Tooltip to icon-only buttons so the action is discoverable — the
 *   icon alone may not be clear enough.
 * - Set `triggerAsChild` when wrapping interactive elements (buttons, links) to
 *   avoid nesting a `<button>` inside a `<button>` (invalid HTML).
 * - Safe for conditional use — if `content` is nullish, the tooltip is skipped and
 *   children render unwrapped.
 * - Adjust `side` when the trigger is near a screen edge (e.g. `side="bottom"` for
 *   elements near the top of the viewport).
 * - Set `disableHoverablePopup={false}` only when users need to interact with the
 *   tooltip itself (e.g. to select or copy text from it).
 * @see https://base-ui.com/react/components/tooltip Base UI Tooltip documentation
 * @see https://ui.shadcn.com/docs/components/tooltip shadcn/ui Tooltip documentation
 */
import type { DeprecatedLayoutClassName } from "../../types/deprecatedLayoutClassName";

import React from "react";

import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";

import twMerge from "../../utils/twMerge";

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  );
}

interface TooltipRootProps extends TooltipPrimitive.Root.Props {
  delay?: number;
  disableHoverablePopup?: boolean;
}

function TooltipRoot({
  delay,
  disableHoverablePopup,
  ...props
}: TooltipRootProps) {
  return (
    <TooltipProvider delay={delay}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        disableHoverablePopup={disableHoverablePopup}
        {...props}
      />
    </TooltipProvider>
  );
}

interface TooltipTriggerProps<ClassName extends string = string>
  extends Omit<TooltipPrimitive.Trigger.Props, "className"> {
  className?: DeprecatedLayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TooltipTrigger<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TooltipTriggerProps<ClassName>) {
  // NOTE (Ben, 2026-01-14): Only pass className if there's an actual value.
  // When using triggerAsChild with render prop, Base UI merges props onto the child.
  // If we pass className="" (from twMerge with no inputs), it overrides the child's
  // own className, breaking styles like SelectableChip's flex layout.
  const mergedClassName = twMerge(className, UNSAFE_className);

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...(mergedClassName ? { className: mergedClassName } : {})}
      {...props}
    />
  );
}

interface TooltipContentProps<ClassName extends string = string>
  extends Omit<TooltipPrimitive.Popup.Props, "className">,
    Pick<
      TooltipPrimitive.Positioner.Props,
      "align" | "alignOffset" | "side" | "sideOffset"
    > {
  className?: DeprecatedLayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TooltipContent({
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  children,
  className,
  UNSAFE_className,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        // (Andrew, 2026-01-09): Note: tooltips should appear above other overlays like menus/popovers.
        // Menus commonly use `z-[100]`, so keep tooltips higher.
        className="isolate z-[200]"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={twMerge(
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 rounded-md px-3 py-1.5 text-xs bg-default text-sheet z-[200] w-fit max-w-xs origin-(--transform-origin)",
            UNSAFE_className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-default fill-default z-50 data-[side=bottom]:top-1 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

type TooltipBaseProps<ClassName extends string = string> = {
  content: React.ReactNode;
  side?: TooltipContentProps["side"];
  sideOffset?: TooltipContentProps["sideOffset"];
  align?: TooltipContentProps["align"];
  alignOffset?: TooltipContentProps["alignOffset"];
  delay?: number;
  /**
   * Set to true to prevent the tooltip from staying open when hovering over
   * the tooltip content itself.
   */
  disableHoverablePopup?: boolean;
  /**
   * Layout class name applied to the trigger wrapper.
   */
  className?: DeprecatedLayoutClassName<ClassName>;
  /**
   * Escape hatch for non-layout classes on the trigger wrapper.
   */
  UNSAFE_className?: string;
};

type TooltipProps<ClassName extends string = string> =
  | (TooltipBaseProps<ClassName> & {
      /**
       * When true, merges trigger props onto the child element instead of wrapping it.
       * Use this when wrapping interactive elements (buttons, links) to avoid invalid HTML.
       * Requires children to be a single React element.
       */
      triggerAsChild: true;
      // eslint-disable-next-line replo/prefer-props-with-children -- cloneElement requires JSX.Element (Patrick, 2026-03-02)
      children: React.JSX.Element;
    })
  | (TooltipBaseProps<ClassName> &
      React.PropsWithChildren<{
        triggerAsChild?: false;
      }>);

function Tooltip<ClassName extends string>(props: TooltipProps<ClassName>) {
  const {
    content,
    children,
    side = "top",
    sideOffset = 10,
    align = "center",
    alignOffset = 0,
    delay,
    triggerAsChild,
    disableHoverablePopup = true,
    className,
    UNSAFE_className,
  } = props;

  if (!content) {
    return <>{children}</>;
  }

  return (
    <TooltipRoot delay={delay} disableHoverablePopup={disableHoverablePopup}>
      {triggerAsChild ? (
        <TooltipTrigger
          render={children}
          className={className}
          UNSAFE_className={UNSAFE_className}
        />
      ) : (
        <TooltipTrigger
          className={className}
          UNSAFE_className={UNSAFE_className}
        >
          {children}
        </TooltipTrigger>
      )}
      <TooltipContent
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
      >
        {content}
      </TooltipContent>
    </TooltipRoot>
  );
}

export { Tooltip };
